import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useT } from "@/i18n";
import {
  creatorServicesQuery,
  formatUsdCents,
  SERVICE_KINDS,
  type CreatorService,
} from "@/lib/people";

export function CreatorServicesPanel({ userId }: { userId: string | undefined }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data: services } = useQuery(creatorServicesQuery(userId));
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<string>("post");
  const [price, setPrice] = useState("");
  const [days, setDays] = useState("7");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["creator-services", userId] });
  }

  async function add(event: React.FormEvent) {
    event.preventDefault();
    if (!userId) return;
    const cents = Math.round(Number(price) * 100);
    if (!title.trim() || !Number.isFinite(cents) || cents <= 0) {
      toast.error(t("profiles.addTitleAndPrice"));
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from("creator_services").insert({
        user_id: userId,
        title: title.trim(),
        kind,
        description: description.trim() || null,
        price_cents: cents,
        delivery_days: Math.max(1, Number(days) || 7),
      });
      if (error) throw error;
      setTitle("");
      setPrice("");
      setDescription("");
      await refresh();
      toast.success(t("profiles.addedToRateCard"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profiles.couldNotSaveService"));
    } finally {
      setSaving(false);
    }
  }

  async function remove(service: CreatorService) {
    await supabase.from("creator_services").delete().eq("id", service.id);
    await refresh();
  }

  return (
    <section className="card-surface space-y-5 p-6">
      <div>
        <h2 className="font-display text-xl font-bold">{t("profiles.yourRateCard")}</h2>
        <p className="text-sm text-muted-foreground">{t("profiles.rateCardHelperText")}</p>
      </div>

      <div className="space-y-2">
        {(services ?? []).map((s) => (
          <div
            key={s.id}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-4"
          >
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold">{s.title}</p>
              <p className="text-xs text-muted-foreground">
                {SERVICE_KINDS.find((k) => k.value === s.kind)?.label ?? s.kind} ·{" "}
                {t("profiles.deliveryInDays", { n: s.delivery_days })}
              </p>
              {s.description && <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>}
            </div>
            <p className="font-display text-lg font-bold text-gradient-hot">
              {formatUsdCents(s.price_cents)}
            </p>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("profiles.remove")}
              onClick={() => void remove(s)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        {!services?.length && (
          <p className="text-sm text-muted-foreground">{t("profiles.nothingListedYet")}</p>
        )}
      </div>

      <form onSubmit={add} className="grid gap-3 border-t border-border pt-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="svc-title">{t("profiles.service")}</Label>
          <Input
            id="svc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Instagram reel"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="svc-kind">{t("profiles.type")}</Label>
          <select
            id="svc-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
          >
            {SERVICE_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="svc-price">{t("profiles.priceUsd")}</Label>
          <Input
            id="svc-price"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="250"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="svc-days">{t("profiles.deliveryDays")}</Label>
          <Input id="svc-days" value={days} onChange={(e) => setDays(e.target.value)} />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="svc-desc">{t("profiles.whatBrandGets")}</Label>
          <Textarea
            id="svc-desc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <Button type="submit" variant="hot" disabled={saving}>
            <Plus className="size-4" /> {saving ? t("profiles.saving") : t("profiles.addService")}
          </Button>
        </div>
      </form>
    </section>
  );
}
